import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useBranding } from '@/contexts/BrandingContext';
import { isOwner } from '@/lib/perms';
import WhatsAppDeepLinkButton from '@/components/WhatsAppDeepLinkButton';
import { Download, Save, X, FileText, MessageSquare, BookmarkPlus } from 'lucide-react';
import { useConfirm } from '@/components/ConfirmDialog';
import { generateProposalPdf } from '@/lib/letterheadPdf';
import PdfPreview from '@/components/PdfPreview';
import ProposalCanvas, { buildDefaultBoxes, convertToBoxes, applyTemplateBoxes } from '@/components/ProposalCanvas';

function formatINR(n) { if (!n) return '0'; return Number(n).toLocaleString('en-IN'); }

function formatSaved(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const date = d.toLocaleDateString('en-GB'); // DD/MM/YYYY
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${date} at ${time}`;
}

const defaultScopeItems = [
  'Space planning & layout design',
  '3D visualization & renders',
  'Material selection & specification',
  'Furniture design & procurement',
  'Lighting design',
  'Ceiling & flooring design',
  'Kitchen & wardrobe design',
  'Bathroom design',
  'Site supervision & coordination',
  'Final styling & handover',
];

const defaultTerms = [
  'Design fees are non-refundable once work has commenced.',
  'Any changes to the approved scope will be charged additionally.',
  'All payments to be made as per the agreed schedule.',
  'Material costs are approximate and subject to market variation.',
  'Timeline is subject to material availability and site conditions.',
  'Interior Story retains the right to photograph the completed project for portfolio use.',
];

// Legacy proposals (saved before the page-builder) carry scope/terms/notes as
// separate fields; turn them into an equivalent box layout once, on load.
function boxesFromLegacy(p) {
  return buildDefaultBoxes({
    title: p.title || 'Design Proposal',
    date: p.date,
    client_name: p.client_name,
    re: p.re,
    quotation_note: p.quotation_note,
    scope_heading: p.scope_heading,
    scope_items: (p.scope_items || []).filter(s => s.included !== false).map(s => s.text || s).filter(Boolean),
    terms: (p.terms || []).filter(t => (typeof t === 'string' ? true : t.included !== false)).map(t => (typeof t === 'string' ? t : t.text)).filter(Boolean),
    closing_note: p.closing_note,
  });
}

export default function ProposalBuilder({ lead, proposalId, onSave, onClose }) {
  const [proposal, setProposal] = useState({
    id: '',
    title: 'Design Proposal',
    client_name: lead?.client_name || '',
    client_email: lead?.email || '',
    project_type: lead?.project_type || 'Residential',
    reference: `IS-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 9000) + 1000)}`,
    date: new Date().toLocaleDateString('en-GB'),
    re: '',
    sign_name: '',
    fee_table: [
      { id: 1, description: 'Interior design fees (Design Consultation)', qty: '', rate: 'L.S.', price: 0 },
    ],
    gst_included: false,
    gst_rate: 18,
    total_amount: 0,
    status: 'Draft',
  });
  const [confirm, confirmDialog] = useConfirm();
  const [boxes, setBoxes] = useState([]);
  const [pageCount, setPageCount] = useState(1);
  const { user } = useAuth();
  const { branding } = useBranding();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [showWhatsApp, setShowWhatsApp] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  useEffect(() => { api.get('/settings').then(r => setSettings(r.data)).catch(() => {}); }, []);

  // Seed a NEW proposal from the reusable letterhead template (Settings → Proposal Template).
  useEffect(() => {
    if (proposalId || !settings) return; // only for new proposals
    const t = settings.proposal_templates || {};
    setProposal(prev => ({
      ...prev,
      re: prev.re || t.re_default || '',
      sign_name: prev.sign_name || t.sign_name || '',
    }));
    const ctx = {
      title: 'Design Proposal',
      date: new Date().toLocaleDateString('en-GB'),
      client_name: lead?.client_name || '',
      re: t.re_default || '',
    };
    // Prefer the saved layout template (Settings → Documents); fall back to the
    // built-in letterhead layout when none has been saved yet.
    const saved = settings.proposal_layout;
    if (Array.isArray(saved?.boxes) && saved.boxes.length) {
      setBoxes(prev => (prev.length ? prev : applyTemplateBoxes(saved.boxes, ctx)));
      setPageCount(prev => (prev > 1 ? prev : (saved.page_count || 2)));
      return;
    }
    const seeded = buildDefaultBoxes({
      ...ctx,
      quotation_note: t.quotation_note ?? 'The above mentioned quotation is a lump sum amount for the design services, this does not include material and labour cost.',
      scope_heading: t.scope_heading || 'Scope of Work - Design Consultation',
      scope_items: t.default_scope_items || defaultScopeItems,
      terms: t.default_terms || defaultTerms,
      closing_note: t.closing_note ?? 'I trust you will find this to your satisfaction. I look forward to working with you. Thank you.',
    });
    setBoxes(prev => (prev.length ? prev : seeded.boxes));
    setPageCount(prev => (prev > 1 ? prev : seeded.pageCount));
  }, [settings, proposalId, lead]);

  const saveAsTemplate = async () => {
    if (!(await confirm({
      title: 'Save as proposal template?',
      message: 'New proposals will start from this exact layout — boxes, text, headings and fee-table settings. Your existing proposals are unaffected.',
      confirmLabel: 'Save template',
      destructive: false,
    }))) return;
    try {
      await api.put('/settings', { proposal_layout: { boxes, page_count: pageCount } });
      setSettings(prev => ({ ...prev, proposal_layout: { boxes, page_count: pageCount } }));
      toast.success('Saved as the default proposal template');
    } catch { toast.error('Could not save template'); }
  };

  // Load a specific proposal when editing/revising; blank when creating new.
  useEffect(() => {
    if (!proposalId) return; // new proposal → keep blank defaults
    const existing = lead?.proposals?.find(p => p.id === proposalId);
    if (existing) {
      setProposal(prev => ({
        ...prev,
        ...existing,
        fee_table: existing.fee_table || prev.fee_table,
        status: existing.status || prev.status,
      }));
      const saved = Array.isArray(existing.blocks) ? existing.blocks : [];
      if (saved.some(b => b.x != null)) {
        // current free-box format
        const bx = saved.filter(b => b.x != null);
        setBoxes(bx);
        setPageCount(existing.page_count || Math.max(1, ...bx.map(b => (Number(b.page) || 0) + 1)));
      } else if (saved.length) {
        // older flow-block format: stack into boxes + re-create the header boxes
        const conv = convertToBoxes(saved);
        const hdr = buildDefaultBoxes({
          title: existing.title || 'Design Proposal',
          date: existing.date || new Date().toLocaleDateString('en-GB'),
          client_name: existing.client_name || lead?.client_name || '',
          re: existing.re || '',
        }).boxes.slice(0, 3);
        setBoxes([...hdr, ...conv.boxes]);
        setPageCount(conv.pageCount);
      } else {
        const conv = boxesFromLegacy({ ...existing, client_name: existing.client_name || lead?.client_name });
        setBoxes(conv.boxes);
        setPageCount(conv.pageCount);
      }
      setLastSavedAt(existing.updated_at || existing.created_at || null);
    }
  }, [lead, proposalId]);

  const updateFeeRow = (idx, key, value) => {
    setProposal(prev => {
      const updated = [...prev.fee_table];
      updated[idx] = { ...updated[idx], [key]: value };
      if (key === 'qty' || key === 'rate') {
        const q = Number(updated[idx].qty), rt = Number(updated[idx].rate);
        if (!isNaN(q) && !isNaN(rt) && updated[idx].qty !== '' && updated[idx].rate !== '') {
          updated[idx].price = q * rt;
        }
      }
      const sub = updated.reduce((s, r) => s + (Number(r.price) || 0), 0);
      const gstAmt = prev.gst_included ? Math.round(sub * prev.gst_rate / 100) : 0;
      return { ...prev, fee_table: updated, total_amount: sub + gstAmt };
    });
  };

  const addFeeRow = () => {
    setProposal(prev => ({
      ...prev,
      fee_table: [...prev.fee_table, { id: Date.now(), description: '', qty: '', rate: 'L.S.', price: 0 }]
    }));
  };

  const removeFeeRow = (idx) => {
    setProposal(prev => {
      const updated = prev.fee_table.filter((_, i) => i !== idx);
      const sub = updated.reduce((s, r) => s + (Number(r.price) || 0), 0);
      const gstAmt = prev.gst_included ? Math.round(sub * prev.gst_rate / 100) : 0;
      return { ...prev, fee_table: updated, total_amount: sub + gstAmt };
    });
  };

  const subtotal = proposal.fee_table.reduce((s, r) => s + (Number(r.price) || 0), 0);
  const gstAmount = proposal.gst_included ? Math.round(subtotal * proposal.gst_rate / 100) : 0;
  const grandTotal = subtotal + gstAmount;
  const showBank = lead?.status === 'Won'; // bank details only on Won proposals

  const firstBullets = (() => {
    const html = boxes.map(b => b.html || '').find(h => h.includes('<li'));
    if (!html) return [];
    return (html.match(/<li[^>]*>([\s\S]*?)<\/li>/g) || [])
      .map(li => li.replace(/<[^>]+>/g, '').trim()).filter(Boolean);
  })();
  const whatsappMessage =
    `Hi ${proposal.client_name || 'there'},\n\n` +
    `Sharing your design proposal *${proposal.title}* (Ref: ${proposal.reference}).\n\n` +
    (firstBullets.length ? `Scope highlights:\n` +
      firstBullets.slice(0, 4).map(s => `• ${s}`).join('\n') +
      (firstBullets.length > 4 ? `\n• …and ${firstBullets.length - 4} more` : '') + '\n\n' : '') +
    `Estimated total: ₹${formatINR(grandTotal)}${proposal.gst_included ? ' (incl. GST)' : ''}\n\n` +
    `Looking forward to your thoughts!\n\n— Juthika · Interior Story`;

  if (!isOwner(user)) return null; // proposals are Owner-only

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        title: proposal.title,
        re: proposal.re,
        sign_name: proposal.sign_name,
        fee_table: proposal.fee_table,
        blocks: boxes,
        page_count: pageCount,
        total_amount: grandTotal,
        gst_included: proposal.gst_included,
        gst_rate: proposal.gst_rate,
        status: proposal.status,
      };
      if (proposal.id) {
        await api.put(`/leads/${lead.id}/proposals/${proposal.id}`, payload);
      } else {
        // First save creates the draft; capture its id so subsequent saves
        // UPDATE it instead of spawning duplicate drafts.
        const res = await api.post(`/leads/${lead.id}/proposals`, payload);
        if (res.data?.id) {
          setProposal(prev => ({ ...prev, id: res.data.id, version: res.data.version, root_id: res.data.root_id }));
        }
      }
      setLastSavedAt(new Date().toISOString());
      toast.success('Proposal saved');
      if (onSave) onSave();
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const buildDoc = async () => {
    const data = {
      doc_title: proposal.title || 'Design Proposal',
      date: proposal.date,
      client_name: proposal.client_name,
      re: proposal.re,
      fee_table: proposal.fee_table,
      subtotal,
      gst_included: proposal.gst_included,
      gst_rate: proposal.gst_rate,
      gst_amount: gstAmount,
      total_amount: grandTotal,
      sign_name: proposal.sign_name,
      blocks: boxes,
      page_count: pageCount,
    };
    return generateProposalPdf({
      proposal: data,
      branding,
      letterhead: settings?.letterhead,
      bank: settings?.bank_details,
      showBank,
    });
  };

  const generatePDF = async () => {
    try {
      const doc = await buildDoc();
      doc.save(`Proposal-${(proposal.client_name || 'client').replace(/\s+/g, '_')}.pdf`);
      toast.success(showBank ? 'PDF downloaded (bank details included)' : 'PDF downloaded');
    } catch (e) {
      console.error('Proposal PDF failed:', e);
      toast.error('PDF generation failed');
    }
  };

  const openPreview = async () => {
    try {
      const doc = await buildDoc();
      setPreviewData(doc.output('arraybuffer'));
    } catch (e) {
      console.error('Proposal preview failed:', e);
      toast.error('Preview failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="proposal-builder">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-['Manrope'] font-bold text-[#2C2C2E] flex items-center gap-2">
            Proposal Builder
            {proposal.version > 1 && <Badge className="bg-[#3A8C8C]/10 text-[#3A8C8C] text-[10px]" data-testid="proposal-version-badge">v{proposal.version}</Badge>}
          </h3>
          <p className="text-xs text-[#6B7280] mt-0.5">For {proposal.client_name} &middot; {proposal.project_type}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex gap-2">
            <button onClick={() => setShowWhatsApp(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#25D366]/10 text-[#1B7E3B] text-xs font-medium hover:bg-[#25D366]/20 transition-colors" data-testid="proposal-whatsapp-btn">
              <MessageSquare size={13} strokeWidth={1.5} /> WhatsApp
            </button>
            <button onClick={openPreview} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#E8E6E1] text-[#2C2C2E] text-xs font-medium hover:bg-[#D4D0C8] transition-colors" data-testid="proposal-preview-btn">
              <FileText size={13} strokeWidth={1.5} /> Preview
            </button>
            <button onClick={generatePDF} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#E8E6E1] text-[#2C2C2E] text-xs font-medium hover:bg-[#D4D0C8] transition-colors" data-testid="proposal-download-pdf">
              <Download size={13} strokeWidth={1.5} /> PDF
            </button>
            <button onClick={saveAsTemplate} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#E8E6E1] text-[#2C2C2E] text-xs font-medium hover:bg-[#D4D0C8] transition-colors" data-testid="proposal-save-template" title="Make this layout the starting point for new proposals">
              <BookmarkPlus size={13} strokeWidth={1.5} /> Save as Template
            </button>
            <button onClick={handleSave} disabled={saving} className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-[#3A8C8C] text-white text-xs font-medium hover:bg-[#2E7A7A] transition-colors disabled:opacity-50" data-testid="proposal-save-btn">
              <Save size={13} strokeWidth={1.5} /> {saving ? 'Saving...' : 'Save'}
            </button>
            {onClose && <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[#E8E6E1] transition-colors"><X size={16} strokeWidth={1.5} /></button>}
          </div>
          {lastSavedAt && <span className="text-[10px] text-[#6B7280]" data-testid="proposal-last-saved">Last saved: {formatSaved(lastSavedAt)}</span>}
        </div>
      </div>

      {/* Document fields */}
      <div className="glass-card rounded-xl p-5 space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div><Label className="text-[10px] text-[#6B7280] uppercase">Title</Label><Input value={proposal.title} onChange={e => setProposal({...proposal, title: e.target.value})} className="mt-1 h-9 rounded-lg border-[#D4D0C8] text-sm" /></div>
          <div><Label className="text-[10px] text-[#6B7280] uppercase">Reference</Label><Input value={proposal.reference} onChange={e => setProposal({...proposal, reference: e.target.value})} className="mt-1 h-9 rounded-lg border-[#D4D0C8] text-sm" /></div>
          <div><Label className="text-[10px] text-[#6B7280] uppercase">Date</Label><Input value={proposal.date} onChange={e => setProposal({...proposal, date: e.target.value})} className="mt-1 h-9 rounded-lg border-[#D4D0C8] text-sm" /></div>
          <div><Label className="text-[10px] text-[#6B7280] uppercase">Status</Label>
            <select value={proposal.status} onChange={e => setProposal({...proposal, status: e.target.value})} data-testid="proposal-status-select"
              className="mt-1 h-9 w-full rounded-lg border border-[#D4D0C8] text-sm px-2 bg-white outline-none focus:border-[#3A8C8C]">
              <option value="Draft">Draft</option>
              <option value="Sent">Sent</option>
              <option value="Approved">Approved</option>
            </select>
          </div>
        </div>
        <div>
          <Label className="text-[10px] text-[#6B7280] uppercase">Re: (subject line)</Label>
          <Input value={proposal.re || ''} onChange={e => setProposal({ ...proposal, re: e.target.value })} placeholder="e.g. Quote for Retail & Kids Activity Centre."
            className="mt-1 h-9 rounded-lg border-[#D4D0C8] text-sm" data-testid="proposal-re-input" />
        </div>
        <div className={`text-[11px] rounded-lg px-3 py-2 ${showBank ? 'bg-[#7DA68A]/10 text-[#5c7d6a]' : 'bg-[#E8E6E1]/60 text-[#6B7280]'}`} data-testid="proposal-bank-hint">
          {showBank
            ? 'Lead is Won — your studio bank details (Settings) will be included in the PDF.'
            : 'Bank details are added automatically once the lead is marked Won. Set them in Settings → Bank Details.'}
        </div>
      </div>

      {/* Page-builder canvas */}
      <ProposalCanvas
        boxes={boxes} setBoxes={setBoxes} pageCount={pageCount} setPageCount={setPageCount}
        proposal={proposal} setProposal={setProposal}
        showBank={showBank} bank={settings?.bank_details}
        subtotal={subtotal} gstAmount={gstAmount} grandTotal={grandTotal}
        updateFeeRow={updateFeeRow} addFeeRow={addFeeRow} removeFeeRow={removeFeeRow}
      />

      <Dialog open={!!previewData} onOpenChange={(o) => { if (!o) setPreviewData(null); }}>
        <DialogContent className="rounded-2xl max-w-3xl" data-testid="proposal-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] flex items-center gap-2"><FileText size={18} strokeWidth={1.5} className="text-[#3A8C8C]" /> Proposal Preview</DialogTitle>
          </DialogHeader>
          <div className="max-h-[75vh] overflow-y-auto rounded-lg bg-[#F1F0EC] p-4">
            <PdfPreview data={previewData} />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showWhatsApp} onOpenChange={setShowWhatsApp}>
        <DialogContent className="rounded-2xl max-w-xl" data-testid="proposal-whatsapp-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] flex items-center gap-2"><MessageSquare size={18} strokeWidth={1.5} className="text-[#1B7E3B]" /> Share Proposal on WhatsApp</DialogTitle>
          </DialogHeader>
          {!lead?.phone && (
            <p className="text-xs text-[#C4727F] mb-1" data-testid="proposal-whatsapp-nophone">This lead has no phone number on file. Add one to the lead to enable sending.</p>
          )}
          <WhatsAppDeepLinkButton
            clientName={proposal.client_name}
            clientPhone={lead?.phone || ''}
            projectName={proposal.title}
            template="custom"
            customMessage={whatsappMessage}
            senderName="Juthika · Interior Story"
            defaultCountry="91"
          />
        </DialogContent>
      </Dialog>

      {confirmDialog}
    </div>
  );
}
